import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Scissors, 
  ArrowLeft, 
  Loader2,
  Clock,
  DollarSign,
  Calendar,
  Edit,
  Trash2,
  Plus,
  FileText,
  Image,
  Receipt,
  Timer,
  Upload,
  Star,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronRight,
  ListTodo,
  CheckCircle2,
  Share2,
  Link,
  Copy,
  Check,
  BookOpen,
  Download,
  FolderArchive,
  ImageIcon,
  ClipboardPaste,
  ShoppingBag,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';

const CosplayDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cosplay, setCosplay] = useState(null);
  const [sfPromoDismissed, setSfPromoDismissed] = useState(false);
  const { user } = useAuth();
  const [kitPricing, setKitPricing] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [qName, setQName] = useState('');
  const [qEmail, setQEmail] = useState('');
  const [qMsg, setQMsg] = useState('');
  const [qSubmitting, setQSubmitting] = useState(false);
  useEffect(() => {
    if (id) setSfPromoDismissed(localStorage.getItem(`sf_promo_dismissed_${id}`) === '1');
  }, [id]);
  const dismissSfPromo = () => {
    if (id) localStorage.setItem(`sf_promo_dismissed_${id}`, '1');
    setSfPromoDismissed(true);
  };
  useEffect(() => { api.get('/kit-pricing').then((r) => setKitPricing(r.data)).catch(() => {}); }, []);
  const openQuote = () => { setQName(user?.display_name || ''); setQEmail(user?.email || ''); setQuoteOpen(true); };
  const submitQuote = async () => {
    if (!qName.trim() || !qEmail.trim()) { toast.error('Name and email are required.'); return; }
    setQSubmitting(true);
    try {
      const res = await api.post('/quote-request', { tier: cosplay?.mandomaster_tier || 'neither', complete: !!cosplay?.mandomaster_kit_complete, client_name: qName, client_email: qEmail, message: qMsg, armor_list: cosplay?.mandomaster_armor_list || '' });
      setQuoteOpen(false); setQMsg('');
      toast.success(`Quote requested${res.data?.quote_number ? ` (${res.data.quote_number})` : ''} — The Star Forge will be in touch!`);
    } catch {
      toast.error('Could not submit your quote. Please try again.');
    } finally { setQSubmitting(false); }
  };
  const [notes, setNotes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [shareStatus, setShareStatus] = useState({ is_public: false, share_token: '', share_url: '' });
  const [communityStatus, setCommunityStatus] = useState({ shared_to_community: false });
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Export Modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState(null); // 'pdf', 'zip-with-notes', 'zip-separate-notes'
  
  // Build Book
  const [showBuildBookModal, setShowBuildBookModal] = useState(false);
  const [buildBookStatus, setBuildBookStatus] = useState({ status: 'idle', progress: 0, message: '' });
  const buildBookPollingRef = useRef(null);
  
  // ZIP Export
  const [zipExportStatus, setZipExportStatus] = useState({ status: 'idle', progress: 0, message: '' });
  const zipExportPollingRef = useRef(null);
  
  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showTaskCompleteModal, setShowTaskCompleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [noteForm, setNoteForm] = useState({ content: '', category: 'general' });
  const [receiptForm, setReceiptForm] = useState({ description: '', amount: '', vendor: '', category: 'materials', purchase_date: '' });
  const [timeLogForm, setTimeLogForm] = useState({ hours: '', description: '', work_date: '' });
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [taskForm, setTaskForm] = useState({ name: '', categoryId: '' });
  const [photoForm, setPhotoForm] = useState({ file: null, caption: '', is_cover: false });
  const [taskCompleteForm, setTaskCompleteForm] = useState({ task: null, categoryId: '', photos: [], caption: '' });
  
  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [cosplayRes, notesRes, photosRes, receiptsRes, timeLogsRes, taskCategoriesRes, shareRes, communityRes] = await Promise.all([
        api.get(`/cosplays/${id}`),
        api.get(`/cosplays/${id}/notes`),
        api.get(`/cosplays/${id}/photos`),
        api.get(`/cosplays/${id}/receipts`),
        api.get(`/cosplays/${id}/time-logs`),
        api.get(`/cosplays/${id}/task-categories`),
        api.get(`/cosplays/${id}/share`).catch(() => ({ data: { is_public: false, share_token: '', share_url: '' } })),
        api.get(`/cosplays/${id}/community-status`).catch(() => ({ data: { shared_to_community: false } }))
      ]);
      
      setCosplay(cosplayRes.data);
      setNotes(notesRes.data);
      setPhotos(photosRes.data);
      setReceipts(receiptsRes.data);
      setTimeLogs(timeLogsRes.data);
      setTaskCategories(taskCategoriesRes.data);
      setEditForm(cosplayRes.data);
      setShareStatus(shareRes.data);
      setCommunityStatus(communityRes.data);
      
      // Auto-expand all categories
      const expanded = {};
      taskCategoriesRes.data.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load cosplay');
      navigate('/cosplays');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateCosplay = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.put(`/cosplays/${id}`, {
        ...editForm,
        budget: editForm.budget ? parseFloat(editForm.budget) : 0
      });
      toast.success('Cosplay updated!');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update cosplay');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleShare = async () => {
    try {
      const response = await api.post(`/cosplays/${id}/share`);
      setShareStatus(response.data);
      if (response.data.is_public) {
        toast.success('Sharing enabled! Copy the link to share.');
      } else {
        toast.success('Sharing disabled');
      }
    } catch (error) {
      toast.error('Failed to update share settings');
    }
  };

  const handleToggleCommunityShare = async () => {
    try {
      const response = await api.post(`/cosplays/${id}/share-community`);
      setCommunityStatus({ shared_to_community: response.data.shared });
      if (response.data.shared) {
        toast.success('Shared to community! Others can now see and comment on your build.');
      } else {
        toast.success('Removed from community');
      }
    } catch (error) {
      toast.error('Failed to update community share settings');
    }
  };

  const copyShareLink = () => {
    const fullUrl = `${window.location.origin}${shareStatus.share_url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Build Book functions
  const startBuildBookGeneration = async () => {
    try {
      setBuildBookStatus({ status: 'generating', progress: 0, message: 'Starting...' });
      
      await api.post(`/cosplays/${id}/build-book`);
      
      // Start polling for status
      buildBookPollingRef.current = setInterval(async () => {
        try {
          const response = await api.get(`/cosplays/${id}/build-book/status`);
          setBuildBookStatus(response.data);
          
          if (response.data.status === 'complete' || response.data.status === 'error') {
            clearInterval(buildBookPollingRef.current);
            buildBookPollingRef.current = null;
          }
        } catch (error) {
          console.error('Error polling status:', error);
        }
      }, 500);
      
    } catch (error) {
      toast.error('Failed to start build book generation');
      setBuildBookStatus({ status: 'error', progress: 0, message: 'Failed to start' });
    }
  };

  const downloadBuildBook = async () => {
    try {
      const response = await api.get(`/cosplays/${id}/build-book/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cosplay?.title || 'Cosplay'}_BuildBook.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Build book downloaded!');
    } catch (error) {
      toast.error('Failed to download build book');
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (buildBookPollingRef.current) {
        clearInterval(buildBookPollingRef.current);
      }
      if (zipExportPollingRef.current) {
        clearInterval(zipExportPollingRef.current);
      }
    };
  }, []);

  // ZIP Export functions
  const startZipExport = async (includeNotesInImage) => {
    try {
      setZipExportStatus({ status: 'generating', progress: 0, message: 'Starting...' });
      
      await api.post(`/cosplays/${id}/export-zip`, {
        include_notes_in_image: includeNotesInImage
      });
      
      // Start polling for status
      zipExportPollingRef.current = setInterval(async () => {
        try {
          const response = await api.get(`/cosplays/${id}/export-zip/status`);
          setZipExportStatus(response.data);
          
          if (response.data.status === 'complete' || response.data.status === 'error') {
            clearInterval(zipExportPollingRef.current);
            zipExportPollingRef.current = null;
          }
        } catch (error) {
          console.error('Error polling ZIP status:', error);
        }
      }, 500);
      
    } catch (error) {
      toast.error('Failed to start ZIP export');
      setZipExportStatus({ status: 'error', progress: 0, message: 'Failed to start' });
    }
  };

  const downloadZipExport = async () => {
    try {
      const response = await api.get(`/cosplays/${id}/export-zip/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cosplay?.title || 'Cosplay'}_Images.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('ZIP file downloaded!');
    } catch (error) {
      toast.error('Failed to download ZIP file');
    }
  };

  // Handle export selection
  const handleExportSelection = (type) => {
    setExportType(type);
    setShowExportModal(false);
    setShowBuildBookModal(true);
    
    if (type === 'pdf') {
      startBuildBookGeneration();
    } else if (type === 'zip-with-notes') {
      startZipExport(true);
    } else if (type === 'zip-separate-notes') {
      startZipExport(false);
    }
  };

  const handleDeleteCosplay = async () => {
    try {
      await api.delete(`/cosplays/${id}`);
      toast.success('Cosplay deleted');
      navigate('/cosplays');
    } catch (error) {
      toast.error('Failed to delete cosplay');
    }
  };

  const openAddNoteModal = () => {
    setEditingNoteId(null);
    setNoteForm({ content: '', category: 'general' });
    setShowNoteModal(true);
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setNoteForm({ content: note.content, category: note.category || 'general' });
    setShowNoteModal(true);
  };

  const handleNoteModalChange = (open) => {
    setShowNoteModal(open);
    if (!open) {
      setEditingNoteId(null);
      setNoteForm({ content: '', category: 'general' });
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingNoteId) {
        await api.put(`/cosplays/${id}/notes/${editingNoteId}`, noteForm);
        toast.success('Note updated!');
      } else {
        await api.post(`/cosplays/${id}/notes`, noteForm);
        toast.success('Note added!');
      }
      setShowNoteModal(false);
      setEditingNoteId(null);
      setNoteForm({ content: '', category: 'general' });
      fetchData();
    } catch (error) {
      toast.error(editingNoteId ? 'Failed to update note' : 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/cosplays/${id}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleAddReceipt = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post(`/cosplays/${id}/receipts`, {
        ...receiptForm,
        amount: parseFloat(receiptForm.amount)
      });
      toast.success('Receipt added!');
      setShowReceiptModal(false);
      setReceiptForm({ description: '', amount: '', vendor: '', category: 'materials', purchase_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add receipt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    try {
      await api.delete(`/cosplays/${id}/receipts/${receiptId}`);
      toast.success('Receipt deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  const handleAddTimeLog = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post(`/cosplays/${id}/time-logs`, {
        ...timeLogForm,
        hours: parseFloat(timeLogForm.hours)
      });
      toast.success('Time logged!');
      setShowTimeLogModal(false);
      setTimeLogForm({ hours: '', description: '', work_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to log time');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTimeLog = async (logId) => {
    try {
      await api.delete(`/cosplays/${id}/time-logs/${logId}`);
      toast.success('Time log deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete time log');
    }
  };

  // Clipboard image support — collect references by pasting (e.g. "Copy image" from Google Images).
  // A pasted Blob carries no filename, so we synthesize one with a valid extension because the backend
  // derives the stored file extension from file.filename and validates against the image MIME type.
  const MIME_EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };
  const blobToImageFile = (blob) => {
    const ext = MIME_EXT[blob.type] || 'png';
    return new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type });
  };

  // Returns the first image File found in a clipboard event, or null.
  const extractClipboardImage = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) return blobToImageFile(blob);
        }
      }
    }
    const files = e.clipboardData?.files;
    if (files) {
      for (const f of files) {
        if (f.type.startsWith('image/')) return blobToImageFile(f);
      }
    }
    return null;
  };

  // Paste handler for the open Add Photo modal — Ctrl+V sets/replaces the staged image.
  const handleModalPaste = (e) => {
    const file = extractClipboardImage(e);
    if (!file) return;
    e.preventDefault();
    setPhotoForm((f) => ({ ...f, file }));
  };

  // Document-level paste, active only on the Photos tab: capture a clipboard image and open the modal
  // pre-filled so the existing caption / set-as-cover flow is preserved. No image → no-op (text/URL
  // paste passes through natively, so typing/pasting in the caption field is unaffected).
  useEffect(() => {
    if (activeTab !== 'photos') return undefined;
    const onPaste = (e) => {
      const file = extractClipboardImage(e);
      if (!file) return;
      e.preventDefault();
      setPhotoForm((f) => ({ ...f, file }));
      setShowPhotoModal(true);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [activeTab]);

  // Preview URL for the staged photo (covers both file-picker and pasted images); revoke on change.
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  useEffect(() => {
    if (!photoForm.file) {
      setPhotoPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(photoForm.file);
    setPhotoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoForm.file]);

  const handlePhotoUpload = async (e) => {
    e?.preventDefault();
    const file = photoForm.file;
    if (!file) return;
    
    setSubmitting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', photoForm.caption || '');
    formData.append('is_cover', photoForm.is_cover ? 'true' : 'false');
    formData.append('task_id', '');
    
    try {
      await api.post(`/cosplays/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded!');
      setShowPhotoModal(false);
      setPhotoForm({ file: null, caption: '', is_cover: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTaskComplete = async (categoryId, task, shouldComplete) => {
    if (shouldComplete && !task.completed) {
      // Opening completion modal to optionally add photos
      setTaskCompleteForm({ task, categoryId, photos: [], caption: '' });
      setShowTaskCompleteModal(true);
    } else {
      // Just toggle off (uncomplete)
      try {
        await api.put(`/cosplays/${id}/task-categories/${categoryId}/tasks/${task.id}`, {
          completed: false
        });
        fetchData();
      } catch (error) {
        toast.error('Failed to update task');
      }
    }
  };

  const handleConfirmTaskComplete = async () => {
    const { task, categoryId, photos, caption } = taskCompleteForm;
    setSubmitting(true);
    
    try {
      // Upload photos if any
      for (const file of photos) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('caption', caption || `Completed: ${task.name}`);
        formData.append('is_cover', 'false');
        formData.append('task_id', task.id);
        
        await api.post(`/cosplays/${id}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      // Mark task as complete
      await api.put(`/cosplays/${id}/task-categories/${categoryId}/tasks/${task.id}`, {
        completed: true
      });
      
      toast.success('Task completed!' + (photos.length > 0 ? ` ${photos.length} photo(s) added.` : ''));
      setShowTaskCompleteModal(false);
      setTaskCompleteForm({ task: null, categoryId: '', photos: [], caption: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to complete task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetCover = async (photoId) => {
    try {
      await api.put(`/cosplays/${id}/photos/${photoId}/set-cover`);
      toast.success('Cover photo updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to set cover');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await api.delete(`/cosplays/${id}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleProgressChange = async (value) => {
    try {
      await api.put(`/cosplays/${id}`, { progress: value[0] });
      setCosplay(prev => ({ ...prev, progress: value[0] }));
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  // Task Category handlers
  const handleAddCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post(`/cosplays/${id}/task-categories`, categoryForm);
      toast.success('Category created!');
      setShowCategoryModal(false);
      setCategoryForm({ name: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await api.delete(`/cosplays/${id}/task-categories/${categoryId}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post(`/cosplays/${id}/task-categories/${taskForm.categoryId}/tasks`, {
        name: taskForm.name
      });
      toast.success('Task added!');
      setShowTaskModal(false);
      setTaskForm({ name: '', categoryId: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleTask = async (categoryId, taskId, currentCompleted, task) => {
    if (!currentCompleted) {
      // Completing task - show modal with photo option
      handleTaskComplete(categoryId, task, true);
    } else {
      // Uncompleting task
      handleTaskComplete(categoryId, task, false);
    }
  };

  const handleDeleteTask = async (categoryId, taskId) => {
    try {
      await api.delete(`/cosplays/${id}/task-categories/${categoryId}/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getTotalTasks = () => {
    return taskCategories.reduce((acc, cat) => acc + cat.total_tasks, 0);
  };

  const getCompletedTasks = () => {
    return taskCategories.reduce((acc, cat) => acc + cat.completed_tasks, 0);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in_progress';
      default: return 'status-planning';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Planning';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!cosplay) return null;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn" data-testid="cosplay-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/cosplays')}
            className="p-2 flex-shrink-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold truncate">{cosplay.title}</h1>
              <span className={`px-2 sm:px-3 py-1 text-xs font-medium flex-shrink-0 ${getStatusClass(cosplay.status)}`}>
                {getStatusLabel(cosplay.status)}
              </span>
            </div>
            <p className="mt-1 text-sm sm:text-base truncate">
              {cosplay.character_name}
              {cosplay.series_name && ` • ${cosplay.series_name}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-auto sm:ml-0">
          <Button
            variant="ghost"
            onClick={() => setShowExportModal(true)}
            className="p-2 sm:p-2.5"
            data-testid="export-btn"
            title="Export Build Book"
          >
            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowShareModal(true)}
            className={`p-2 sm:p-2.5 ${shareStatus.is_public ? 'win-selected' : ''}`}
            data-testid="share-cosplay-btn"
          >
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setEditForm(cosplay); setShowEditModal(true); }}
            className="p-2 sm:p-2.5"
            data-testid="edit-cosplay-btn"
          >
            <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                className="p-2 sm:p-2.5"
                data-testid="delete-cosplay-btn"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-slate-900 border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Cosplay?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this cosplay and all associated notes, photos, receipts, and time logs.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteCosplay}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Purchase from The Star Forge — only on builds imported from MandoMaster */}
      {cosplay.source === 'mandomaster' && !sfPromoDismissed && (
        <div className="relative glass-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <button onClick={dismissSfPromo} aria-label="Dismiss" className="absolute top-2 right-2 z-10">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 min-w-0">
            <ShoppingBag className="w-5 h-5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm sm:text-base">Want this kit made for you?</p>
              <p className="text-xs sm:text-sm">Request a quote from The Star Forge for the full armor set.</p>
              {cosplay.mandomaster_kit_complete && kitPricing?.tiers?.[cosplay.mandomaster_tier] != null && (
                <p className="font-semibold text-sm mt-0.5">Estimated: ~${kitPricing.tiers[cosplay.mandomaster_tier]} <span className="font-normal text-xs">(quote estimate)</span></p>
              )}
            </div>
          </div>
          <Button
            onClick={openQuote}
            data-testid="starforge-purchase-btn"
            className="win-btn-accent font-semibold flex-shrink-0"
          >
            <ShoppingBag className="w-4 h-4 mr-2" /> Request a Quote
          </Button>
        </div>
      )}

      {/* Star Forge quote form */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Quote from The Star Forge</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {cosplay?.mandomaster_kit_complete && kitPricing?.tiers?.[cosplay?.mandomaster_tier] != null && (
              <p className="text-sm">Estimated: <span className="font-bold">~${kitPricing.tiers[cosplay.mandomaster_tier]}</span> — final price confirmed by The Star Forge.</p>
            )}
            <Input placeholder="Your name" value={qName} onChange={(e) => setQName(e.target.value)} />
            <Input placeholder="Email" type="email" value={qEmail} onChange={(e) => setQEmail(e.target.value)} />
            <Textarea placeholder="Anything else? (sizing, timeline, colors…)" rows={3} value={qMsg} onChange={(e) => setQMsg(e.target.value)} />
            <Button onClick={submitQuote} disabled={qSubmitting} className="w-full win-btn-default font-semibold">
              {qSubmitting ? 'Sending…' : 'Send Quote Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <div className="glass-card p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm">Spent / Budget</span>
          </div>
          <p className="text-base sm:text-xl font-mono">
            ${cosplay.total_spent.toLocaleString()}
            <span className="text-xs sm:text-sm"> / ${cosplay.budget.toLocaleString()}</span>
          </p>
        </div>
        <div className="glass-card p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm">Time Logged</span>
          </div>
          <p className="text-base sm:text-xl font-mono">{cosplay.total_time_hours}h</p>
        </div>
        <div className="glass-card p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <ListTodo className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm">Tasks</span>
          </div>
          <p className="text-base sm:text-xl font-mono">
            {getCompletedTasks()}/{getTotalTasks()}
          </p>
        </div>
        <div className="glass-card p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm">Target Date</span>
          </div>
          <p className="text-sm sm:text-lg truncate">
            {cosplay.target_date ? new Date(cosplay.target_date).toLocaleDateString() : 'Not set'}
          </p>
        </div>
      </div>

      {/* Progress Section */}
      <div className="glass-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="font-heading font-semibold text-sm sm:text-base">Progress</h3>
          <span className="font-mono text-xl sm:text-2xl">{cosplay.progress}%</span>
        </div>
        <Slider
          value={[cosplay.progress]}
          onValueCommit={handleProgressChange}
          max={100}
          step={1}
          className="w-full"
          data-testid="progress-slider"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
        <div className="overflow-x-auto overflow-y-hidden -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          <TabsList className="inline-flex w-max sm:w-auto">
            <TabsTrigger 
              value="overview" 
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
              data-testid="tab-overview"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="tasks" 
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
              data-testid="tab-tasks"
            >
              Tasks ({getCompletedTasks()}/{getTotalTasks()})
            </TabsTrigger>
            <TabsTrigger 
              value="notes" 
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
              data-testid="tab-notes"
            >
              Notes ({notes.length})
            </TabsTrigger>
            <TabsTrigger 
              value="photos" 
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
              data-testid="tab-photos"
            >
              Photos ({photos.length})
            </TabsTrigger>
            <TabsTrigger 
              value="receipts" 
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
              data-testid="tab-receipts"
            >
              Receipts
            </TabsTrigger>
            <TabsTrigger 
              value="time" 
              className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
              data-testid="tab-time"
            >
              Time
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cover Image */}
            <div className="glass-card overflow-hidden">
              <div className="aspect-video bg-slate-800/50 relative">
                {cosplay.cover_image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}${cosplay.cover_image}`}
                    alt={cosplay.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <Scissors className="w-16 h-16 mb-3" />
                    <p>No cover photo yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-heading font-semibold">Details</h3>
              {cosplay.description && (
                <div>
                  <p className="text-sm mb-1">Description</p>
                  <p>{cosplay.description}</p>
                </div>
              )}
              {cosplay.target_event && (
                <div>
                  <p className="text-sm mb-1">Target Event</p>
                  <p>{cosplay.target_event}</p>
                </div>
              )}
              <div>
                <p className="text-sm mb-1">Created</p>
                <p>{new Date(cosplay.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setShowCategoryModal(true)}
              variant="outline"
              className="gap-2"
              data-testid="add-category-btn"
            >
              <Plus className="w-5 h-5" />
              Add Category
            </Button>
          </div>

          {taskCategories.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <ListTodo className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold mb-2">No Task Categories Yet</h3>
              <p className="mb-6">Create categories like "Soft Goods", "Armor", "Props" to organize your tasks.</p>
              <Button
                onClick={() => setShowCategoryModal(true)}
                className="win-btn-default gap-2 font-semibold"
              >
                <Plus className="w-5 h-5" />
                Create Category
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {taskCategories.map((category) => (
                <div
                  key={category.id}
                  className="glass-card overflow-hidden"
                  data-testid={`category-${category.id}`}
                >
                  {/* Category Header */}
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleCategoryExpand(category.id)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedCategories[category.id] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                      <div className="flex items-center gap-3">
                        {category.is_complete ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <ListTodo className="w-5 h-5" />
                        )}
                        <h3 className="font-heading font-semibold">
                          {category.name}
                        </h3>
                        {category.is_complete && (
                          <span className="text-xs font-medium">
                            ✓ Complete
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-sm">
                        {category.completed_tasks}/{category.total_tasks}
                      </span>
                      <Button
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTaskForm({ name: '', categoryId: category.id });
                          setShowTaskModal(true);
                        }}
                        className="p-2"
                        data-testid={`add-task-${category.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-900 border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will delete "{category.name}" and all its tasks. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {/* Tasks List */}
                  {expandedCategories[category.id] && (
                    <div className="border-t border-white/10">
                      {category.tasks.length === 0 ? (
                        <div className="p-6 text-center">
                          <p className="text-sm">No tasks yet. Click + to add a task.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-white/5">
                          {category.tasks.map((task) => (
                            <div
                              key={task.id}
                              className="p-4 flex items-center justify-between group"
                              data-testid={`task-${task.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={task.completed}
                                  onCheckedChange={() => handleToggleTask(category.id, task.id, task.completed, task)}
                                  data-testid={`task-checkbox-${task.id}`}
                                />
                                <span className={task.completed ? 'line-through' : ''}>
                                  {task.name}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                onClick={() => handleDeleteTask(category.id, task.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-2"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={openAddNoteModal}
              className="win-btn-default gap-2 font-semibold"
              data-testid="add-note-btn"
            >
              <Plus className="w-5 h-5" />
              Add Note
            </Button>
          </div>
          
          {notes.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4" />
              <p>No notes yet. Add your first note!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notes.map((note) => (
                <div key={note.id} className="glass-card p-6 group" data-testid={`note-${note.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 win-well text-xs capitalize">
                          {note.category}
                        </span>
                        <span className="text-xs">
                          {new Date(note.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap">{note.content}</p>
                      {note.updated_at && note.updated_at !== note.created_at && (
                        <p className="text-xs mt-2 italic">
                          edited {new Date(note.updated_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        onClick={() => handleEditNote(note)}
                        className="p-2"
                        data-testid={`edit-note-${note.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs">
              <ClipboardPaste className="w-4 h-4" />
              Tip: paste (Ctrl+V) to add reference images fast
            </p>
            <Button
              onClick={() => setShowPhotoModal(true)}
              className="win-btn-default gap-2 font-semibold"
              data-testid="add-photo-btn"
            >
              <Upload className="w-5 h-5" />
              Add Photo
            </Button>
          </div>

          {photos.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Image className="w-12 h-12 mx-auto mb-4" />
              <p>No photos yet. Upload your first photo, or paste one with Ctrl+V!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="glass-card overflow-hidden group relative" data-testid={`photo-${photo.id}`}>
                  <div className="aspect-square">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ''}${photo.url}`}
                      alt={photo.caption || 'Photo'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => handleSetCover(photo.id)}
                      className="p-2 text-white"
                    >
                      <Star className={`w-5 h-5 ${photo.is_cover ? 'fill-current' : ''}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="p-2 text-white"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                  {photo.is_cover && (
                    <div className="absolute top-2 left-2 px-2 py-1 win-raised text-xs font-medium">
                      Cover
                    </div>
                  )}
                  {photo.task_name && (
                    <div className="absolute top-2 right-2 px-2 py-1 win-raised text-xs font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {photo.task_name}
                    </div>
                  )}
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-xs text-white truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Receipts Tab */}
        <TabsContent value="receipts" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setShowReceiptModal(true)}
              className="win-btn-default gap-2 font-semibold"
              data-testid="add-receipt-btn"
            >
              <Plus className="w-5 h-5" />
              Add Receipt
            </Button>
          </div>
          
          {receipts.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Receipt className="w-12 h-12 mx-auto mb-4" />
              <p>No receipts yet. Track your expenses!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {receipts.map((receipt) => (
                <div key={receipt.id} className="glass-card p-4 group" data-testid={`receipt-${receipt.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium">{receipt.description}</p>
                        <span className="px-2 py-1 win-well text-xs capitalize">
                          {receipt.category}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        {receipt.vendor && `${receipt.vendor} • `}
                        {new Date(receipt.purchase_date).toLocaleDateString()}
                      </p>
                    </div>
                    <p className="font-mono text-lg">${receipt.amount.toFixed(2)}</p>
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteReceipt(receipt.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Time Tab */}
        <TabsContent value="time" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setShowTimeLogModal(true)}
              className="win-btn-default gap-2 font-semibold"
              data-testid="add-time-btn"
            >
              <Plus className="w-5 h-5" />
              Log Time
            </Button>
          </div>
          
          {timeLogs.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Timer className="w-12 h-12 mx-auto mb-4" />
              <p>No time logged yet. Start tracking!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {timeLogs.map((log) => (
                <div key={log.id} className="glass-card p-4 group" data-testid={`timelog-${log.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p>{log.description || 'Work session'}</p>
                      <p className="text-sm">{new Date(log.work_date).toLocaleDateString()}</p>
                    </div>
                    <p className="font-mono text-lg">{log.hours}h</p>
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteTimeLog(log.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Cosplay
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <p className="text-sm">
              {shareStatus.is_public
                ? 'This cosplay is publicly shared. Anyone with the link can view it.'
                : 'Enable sharing to let others view this cosplay without an account.'}
            </p>

            <div className="flex items-center justify-between p-4 win-well">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <Link className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">Public Link</p>
                  <p className="text-xs">{shareStatus.is_public ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
              <Button
                onClick={handleToggleShare}
              >
                {shareStatus.is_public ? 'Disable' : 'Enable'}
              </Button>
            </div>

            {shareStatus.is_public && shareStatus.share_url && (
              <div className="space-y-2">
                <Label>Share Link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}${shareStatus.share_url}`}
                    className="win-input"
                  />
                  <Button
                    onClick={copyShareLink}
                    className="px-4"
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Community Sharing */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Share with Community</p>
                  <p className="text-xs">
                    {communityStatus.shared_to_community
                      ? 'Visible in the Social tab for all users'
                      : 'Share your build for others to see and comment'}
                  </p>
                </div>
                <Button
                  onClick={handleToggleCommunityShare}
                  data-testid="toggle-community-share-btn"
                >
                  {communityStatus.shared_to_community ? 'Remove from Community' : 'Share to Community'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Edit Cosplay</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateCosplay} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editForm.title || ''}
                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                className="win-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Character</Label>
                <Input
                  value={editForm.character_name || ''}
                  onChange={(e) => setEditForm({...editForm, character_name: e.target.value})}
                  className="win-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Series</Label>
                <Input
                  value={editForm.series_name || ''}
                  onChange={(e) => setEditForm({...editForm, series_name: e.target.value})}
                  className="win-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                className="win-input min-h-[80px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={editForm.status} 
                  onValueChange={(value) => setEditForm({...editForm, status: value})}
                >
                  <SelectTrigger className="win-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  value={editForm.budget || ''}
                  onChange={(e) => setEditForm({...editForm, budget: e.target.value})}
                  className="win-input font-mono"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Event</Label>
                <Input
                  value={editForm.target_event || ''}
                  onChange={(e) => setEditForm({...editForm, target_event: e.target.value})}
                  className="win-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={editForm.target_date || ''}
                  onChange={(e) => setEditForm({...editForm, target_date: e.target.value})}
                  className="win-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowEditModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={handleNoteModalChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">{editingNoteId ? 'Edit Note' : 'Add Note'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveNote} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={noteForm.category} 
                onValueChange={(value) => setNoteForm({...noteForm, category: value})}
              >
                <SelectTrigger className="win-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="materials">Materials</SelectItem>
                  <SelectItem value="techniques">Techniques</SelectItem>
                  <SelectItem value="reference">Reference</SelectItem>
                  <SelectItem value="todo">To Do</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={noteForm.content}
                onChange={(e) => setNoteForm({...noteForm, content: e.target.value})}
                placeholder="Write your note..."
                required
                className="win-input min-h-[120px]"
                data-testid="note-content-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleNoteModalChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
                data-testid="submit-note-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingNoteId ? 'Save Changes' : 'Add Note')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Add Receipt</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddReceipt} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={receiptForm.description}
                onChange={(e) => setReceiptForm({...receiptForm, description: e.target.value})}
                placeholder="What did you buy?"
                required
                className="win-input"
                data-testid="receipt-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  value={receiptForm.amount}
                  onChange={(e) => setReceiptForm({...receiptForm, amount: e.target.value})}
                  placeholder="0.00"
                  required
                  min="0"
                  step="0.01"
                  className="win-input font-mono"
                  data-testid="receipt-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input
                  value={receiptForm.vendor}
                  onChange={(e) => setReceiptForm({...receiptForm, vendor: e.target.value})}
                  placeholder="Store name"
                  className="win-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={receiptForm.category} 
                  onValueChange={(value) => setReceiptForm({...receiptForm, category: value})}
                >
                  <SelectTrigger className="win-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    <SelectItem value="materials">Materials</SelectItem>
                    <SelectItem value="tools">Tools</SelectItem>
                    <SelectItem value="fabric">Fabric</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={receiptForm.purchase_date}
                  onChange={(e) => setReceiptForm({...receiptForm, purchase_date: e.target.value})}
                  className="win-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowReceiptModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
                data-testid="submit-receipt-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Add Receipt'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Time Log Modal */}
      <Dialog open={showTimeLogModal} onOpenChange={setShowTimeLogModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Log Time</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddTimeLog} className="space-y-5 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hours *</Label>
                <Input
                  type="number"
                  value={timeLogForm.hours}
                  onChange={(e) => setTimeLogForm({...timeLogForm, hours: e.target.value})}
                  placeholder="1.5"
                  required
                  min="0.25"
                  step="0.25"
                  className="win-input font-mono"
                  data-testid="time-hours-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={timeLogForm.work_date}
                  onChange={(e) => setTimeLogForm({...timeLogForm, work_date: e.target.value})}
                  className="win-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={timeLogForm.description}
                onChange={(e) => setTimeLogForm({...timeLogForm, description: e.target.value})}
                placeholder="What did you work on?"
                className="win-input min-h-[80px]"
                data-testid="time-description-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowTimeLogModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
                data-testid="submit-time-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log Time'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Add Task Category</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddCategory} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                placeholder="e.g., Soft Goods, Armor, Props, Wig"
                required
                className="win-input"
                data-testid="category-name-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowCategoryModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
                data-testid="submit-category-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Category'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Add Task</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddTask} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Task Name *</Label>
              <Input
                value={taskForm.name}
                onChange={(e) => setTaskForm({...taskForm, name: e.target.value})}
                placeholder="e.g., Cut fabric for bodice"
                required
                className="win-input"
                data-testid="task-name-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowTaskModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
                data-testid="submit-task-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Add Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Modal */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Add Photo
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handlePhotoUpload} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Photo</Label>
              <div
                className={`border-2 p-6 text-center cursor-pointer ${
                  photoForm.file ? 'border-solid' : 'border-dashed'
                }`}
                onClick={() => document.getElementById('photo-file-input').click()}
                onPaste={handleModalPaste}
              >
                <input
                  id="photo-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => setPhotoForm({...photoForm, file: e.target.files[0]})}
                />
                {photoForm.file ? (
                  <div className="space-y-2">
                    {photoPreviewUrl && (
                      <img
                        src={photoPreviewUrl}
                        alt="Preview"
                        className="max-h-40 mx-auto rounded-lg object-contain"
                      />
                    )}
                    <p className="text-xs">Click to change</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 mx-auto" />
                    <p className="text-sm">Click to select a photo, or paste (Ctrl+V)</p>
                    <p className="text-xs">JPEG, PNG, GIF, WebP</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Caption (optional)</Label>
              <Textarea
                value={photoForm.caption}
                onChange={(e) => setPhotoForm({...photoForm, caption: e.target.value})}
                placeholder="Add a note about this photo..."
                className="win-input resize-none"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between p-3 win-well">
              <div>
                <Label>Set as cover photo</Label>
                <p className="text-xs">This photo will be the main image</p>
              </div>
              <Checkbox
                checked={photoForm.is_cover}
                onCheckedChange={(checked) => setPhotoForm({...photoForm, is_cover: checked})}
              />
            </div>

            <Button
              type="submit"
              disabled={!photoForm.file || submitting}
              className="w-full win-btn-default font-semibold"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Upload Photo'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Completion Modal */}
      <Dialog open={showTaskCompleteModal} onOpenChange={setShowTaskCompleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Complete Task
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="p-4 win-well">
              <p className="font-medium">{taskCompleteForm.task?.name}</p>
              <p className="text-xs mt-1">Mark this task as complete</p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Add completion photos (optional)
              </Label>
              <div
                className={`border-2 p-4 text-center cursor-pointer ${
                  taskCompleteForm.photos.length > 0 ? 'border-solid' : 'border-dashed'
                }`}
                onClick={() => document.getElementById('task-photo-input').click()}
              >
                <input
                  id="task-photo-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => setTaskCompleteForm({
                    ...taskCompleteForm, 
                    photos: [...taskCompleteForm.photos, ...Array.from(e.target.files)]
                  })}
                />
                {taskCompleteForm.photos.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {taskCompleteForm.photos.map((file, idx) => (
                        <div key={idx} className="relative">
                          <div className="w-16 h-16 bg-slate-800 flex items-center justify-center overflow-hidden">
                            <img
                              src={URL.createObjectURL(file)}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTaskCompleteForm({
                                ...taskCompleteForm,
                                photos: taskCompleteForm.photos.filter((_, i) => i !== idx)
                              });
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs">Click to add more photos</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="w-6 h-6 mx-auto" />
                    <p className="text-sm">Add photos showing completed work</p>
                  </div>
                )}
              </div>
            </div>
            
            {taskCompleteForm.photos.length > 0 && (
              <div className="space-y-2">
                <Label>Photo caption</Label>
                <Input
                  value={taskCompleteForm.caption}
                  onChange={(e) => setTaskCompleteForm({...taskCompleteForm, caption: e.target.value})}
                  placeholder={`Completed: ${taskCompleteForm.task?.name}`}
                  className="win-input"
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowTaskCompleteModal(false);
                  setTaskCompleteForm({ task: null, categoryId: '', photos: [], caption: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmTaskComplete}
                disabled={submitting}
                className="flex-1 win-btn-default font-semibold"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Complete{taskCompleteForm.photos.length > 0 ? ` with ${taskCompleteForm.photos.length} photo${taskCompleteForm.photos.length > 1 ? 's' : ''}` : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Selection Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-lg mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-heading flex items-center gap-2">
              <Download className="w-5 h-5 flex-shrink-0" />
              <span>Export Options</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-3">
            <p className="text-sm">Choose how you'd like to export your build:</p>

            {/* PDF Export Option */}
            <button
              onClick={() => handleExportSelection('pdf')}
              className="w-full p-3 sm:p-4 text-left group"
              data-testid="export-pdf-option"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 win-well flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold mb-0.5 text-sm sm:text-base">Build Book (PDF)</h3>
                  <p className="text-xs sm:text-sm leading-relaxed">Complete document with photos, notes, tasks, and expenses.</p>
                </div>
              </div>
            </button>

            {/* ZIP with Notes in Image */}
            <button
              onClick={() => handleExportSelection('zip-with-notes')}
              className="w-full p-3 sm:p-4 text-left group"
              data-testid="export-zip-notes-image-option"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 win-well flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold mb-0.5 text-sm sm:text-base">ZIP with Notes on Images</h3>
                  <p className="text-xs sm:text-sm leading-relaxed">Images with notes panel below. Organized by category.</p>
                </div>
              </div>
            </button>

            {/* ZIP with Separate Notes */}
            <button
              onClick={() => handleExportSelection('zip-separate-notes')}
              className="w-full p-3 sm:p-4 text-left group"
              data-testid="export-zip-separate-notes-option"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 win-well flex items-center justify-center flex-shrink-0">
                  <FolderArchive className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold mb-0.5 text-sm sm:text-base">ZIP with Separate Notes</h3>
                  <p className="text-xs sm:text-sm leading-relaxed">Original images + matching text files. Organized by category.</p>
                </div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Progress Modal */}
      <Dialog open={showBuildBookModal} onOpenChange={(open) => {
        const isGenerating = buildBookStatus.status === 'generating' || zipExportStatus.status === 'generating';
        if (!open && !isGenerating) {
          setShowBuildBookModal(false);
          setBuildBookStatus({ status: 'idle', progress: 0, message: '' });
          setZipExportStatus({ status: 'idle', progress: 0, message: '' });
          setExportType(null);
        }
      }}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-heading flex items-center gap-2">
              {exportType === 'pdf' ? (
                <BookOpen className="w-5 h-5 flex-shrink-0" />
              ) : (
                <FolderArchive className="w-5 h-5 flex-shrink-0" />
              )}
              <span>{exportType === 'pdf' ? 'Export Build Book' : 'Export Images'}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 mt-3 sm:mt-4">
            {/* PDF Export States */}
            {exportType === 'pdf' && (
              <>
                {buildBookStatus.status === 'generating' && (
                  <>
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 win-well flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin" />
                      </div>
                      <p className="font-medium mb-1 sm:mb-2 text-sm sm:text-base">Compiling Build Book</p>
                      <p className="text-xs sm:text-sm">{buildBookStatus.message}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Progress value={buildBookStatus.progress} className="h-2" />
                      <p className="text-xs text-center">{buildBookStatus.progress}%</p>
                    </div>
                  </>
                )}
                
                {buildBookStatus.status === 'complete' && (
                  <>
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 win-well flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8" />
                      </div>
                      <p className="font-medium mb-1 sm:mb-2 text-sm sm:text-base">Build Book Ready!</p>
                      <p className="text-xs sm:text-sm">Your build book is ready to download.</p>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        onClick={downloadBuildBook}
                        className="w-full win-btn-default font-semibold gap-2 text-sm sm:text-base"
                        data-testid="download-pdf-btn"
                      >
                        <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                        Download PDF
                      </Button>
                    </div>
                  </>
                )}
                
                {buildBookStatus.status === 'error' && (
                  <>
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 win-well flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <FileText className="w-7 h-7 sm:w-8 sm:h-8" />
                      </div>
                      <p className="font-medium mb-1 sm:mb-2 text-sm sm:text-base">Generation Failed</p>
                      <p className="text-xs sm:text-sm">{buildBookStatus.message || 'An error occurred.'}</p>
                    </div>
                    
                    <Button
                      onClick={startBuildBookGeneration}
                      className="w-full win-btn-default font-semibold text-sm sm:text-base"
                    >
                      Try Again
                    </Button>
                  </>
                )}
              </>
            )}
            
            {/* ZIP Export States */}
            {(exportType === 'zip-with-notes' || exportType === 'zip-separate-notes') && (
              <>
                {zipExportStatus.status === 'generating' && (
                  <>
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 win-well flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin" />
                      </div>
                      <p className="font-medium mb-1 sm:mb-2 text-sm sm:text-base">
                        {exportType === 'zip-with-notes' ? 'Creating Images with Notes' : 'Preparing ZIP File'}
                      </p>
                      <p className="text-xs sm:text-sm">{zipExportStatus.message}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Progress value={zipExportStatus.progress} className="h-2" />
                      <p className="text-xs text-center">{zipExportStatus.progress}%</p>
                    </div>
                  </>
                )}
                
                {zipExportStatus.status === 'complete' && (
                  <>
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 win-well flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8" />
                      </div>
                      <p className="font-medium mb-1 sm:mb-2 text-sm sm:text-base">ZIP Export Ready!</p>
                      <p className="text-xs sm:text-sm">
                        {exportType === 'zip-with-notes' 
                          ? 'Your images with notes are ready.' 
                          : 'Your images and note files are ready.'}
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        onClick={downloadZipExport}
                        className="w-full win-btn-default font-semibold gap-2 text-sm sm:text-base"
                        data-testid="download-zip-btn"
                      >
                        <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                        Download ZIP
                      </Button>
                    </div>
                  </>
                )}
                
                {zipExportStatus.status === 'error' && (
                  <>
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 win-well flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <FolderArchive className="w-7 h-7 sm:w-8 sm:h-8" />
                      </div>
                      <p className="font-medium mb-1 sm:mb-2 text-sm sm:text-base">Export Failed</p>
                      <p className="text-xs sm:text-sm">{zipExportStatus.message || 'An error occurred.'}</p>
                    </div>
                    
                    <Button
                      onClick={() => startZipExport(exportType === 'zip-with-notes')}
                      className="w-full win-btn-default font-semibold text-sm sm:text-base"
                    >
                      Try Again
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CosplayDetailPage;
